import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Sun, Fish, Clock, Package, House, Palette, CloudLightning, Activity, Anchor, CheckCircle2, Database } from "lucide-react";
import { IMAGES, DRONE_SPECS, OPS_STATS } from "@/data";
import TiltImage from "@/components/TiltImage";
import Prototypes from "@/components/Prototypes";
import SystemsExplorer from "@/components/SystemsExplorer";
import PodMap from "@/components/PodMap";
import NightSift from "@/components/NightSift";
import ChapterMark from "@/components/ChapterMark";

const SPEC_ICONS = [Sun, Fish, CloudLightning, Clock, Package, House, Palette];

function CountUp({ target, decimals = 0, suffix = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        const duration = 1600;
        let frame;
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            setValue(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [inView, target]);

    return (
        <span ref={ref}>
            {value.toFixed(decimals)}
            {suffix}
        </span>
    );
}

const LEDGER_METRICS = [
    { label: "Collected today, Meadowbank", value: 41.6, decimals: 1, suffix: " kg", tone: "text-sky-200" },
    { label: "Tonnes collected year to date", value: 18.2, decimals: 1, suffix: " t", tone: "text-sky-200" },
    { label: "Microplastics at Toongabbie Creek", value: 4.7, decimals: 1, suffix: " /m³", tone: "text-sky-200" },
    { label: "Recyclable share of catch", value: 60, decimals: 0, suffix: "%", tone: "text-emerald-300" },
];

export default function Product() {
    return (
        <section id="product" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32 bg-[#041222] border-y border-[#12283C]" data-testid="product-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: "url(/images/harbour-aerial-system.jpg)" }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#041222] via-transparent to-[#041222] pointer-events-none" aria-hidden="true" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <ChapterMark index="03" label="The Product" testid="product-eyebrow" />
                    <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="product-heading">
                        The River Guardian, functional infrastructure for river restoration
                    </h2>
                    <p className="mt-5 text-lg md:text-xl text-slate-400 leading-relaxed" data-testid="product-intro">
                        Meet the River Guardian. Each Guardian is a solar-powered, whale shark-shaped cleaning
                        vessel that skims macro- and micro-plastics while carrying commissioned Indigenous
                        artwork on its hull. Our first fleet, the Eora Guardian, launches on Wallumedegal Country
                        in Ryde.
                    </p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="mt-12 max-w-4xl"
                >
                    <div className="rounded-xl overflow-hidden border border-[#12283C] bg-[#06182A]" data-testid="product-image">
                        <TiltImage
                            src="/images/product-sideview.png"
                            alt="Side-view sketch of the Guardian with the waterline across its hull and rubbish visible in its catchment net below"
                            className="w-full object-cover"
                            testid="product-image-tilt"
                        />
                        <div className="p-6">
                            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">Side view, concept sketch</p>
                            <p className="mt-2 text-base text-slate-300 leading-relaxed">
                                Following our prototype sketch exactly: the waterline cuts the hull, the
                                dot-patterned top rides above the surface, and below the water the deep
                                catchment net holds the night's catch, up to 100 kg or a full cubic metre,
                                plain to see in its stomach.
                            </p>
                        </div>
                    </div>
                </motion.div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="product-specs">
                    {DRONE_SPECS.map((spec, i) => {
                        const Icon = SPEC_ICONS[i];
                        return (
                            <motion.div
                                key={spec.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.45, delay: i * 0.06 }}
                                className="flex gap-4 bg-[#06182A] border border-[#12283C] rounded-xl p-5 hover:border-sky-400/50 transition-colors"
                                data-testid={`product-spec-${i}`}
                            >
                                <span className="w-11 h-11 shrink-0 rounded-lg bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-sky-200" />
                                </span>
                                <div>
                                    <p className="text-lg font-semibold text-slate-100">{spec.title}</p>
                                    <p className="mt-1 text-base text-slate-400 leading-relaxed">{spec.detail}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-px bg-[#12283C] border border-[#12283C] rounded-xl overflow-hidden" data-testid="ops-strip">
                    {OPS_STATS.map((stat, i) => (
                        <div key={stat.label} className="bg-[#06182A] p-5" data-testid={`ops-stat-${i}`}>
                            <p className="font-display font-semibold text-xl text-sky-200">{stat.value}</p>
                            <p className="mt-1 text-sm text-slate-400 leading-snug">{stat.label}</p>
                        </div>
                    ))}
                </div>

                <SystemsExplorer />

                <Prototypes />

                <PodMap />

                <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                    <NightSift />

                    <div data-testid="trash-ledger-widget">
                        <div className="bg-[#06182A] border border-[#12283C] rounded-xl overflow-hidden">
                            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-[#12283C]">
                                <div className="flex items-center gap-3">
                                    <Activity className="w-4 h-4 text-sky-200" />
                                    <p className="font-display font-semibold text-lg" data-testid="ledger-title">Trash Ledger, open catchment data</p>
                                </div>
                                <span className="text-sm px-3 py-1 rounded-full border border-sky-400/40 text-sky-200" data-testid="ledger-demo-badge">
                                    Demonstration feed
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-px bg-[#12283C]">
                                {LEDGER_METRICS.map((metric, i) => (
                                    <div key={metric.label} className="bg-[#06182A] p-6" data-testid={`ledger-metric-${i}`}>
                                        <p className={`font-display font-semibold text-3xl ${metric.tone}`}>
                                            <CountUp target={metric.value} decimals={metric.decimals} suffix={metric.suffix} />
                                        </p>
                                        <p className="mt-1 text-sm text-slate-400">{metric.label}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 px-6 py-4 border-t border-[#12283C] text-sm text-slate-400" data-testid="ledger-fleet-status">
                                <span className="inline-flex items-center gap-2">
                                    <Anchor className="w-3.5 h-3.5 text-sky-200" /> Fleet: 9 active units
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> 0 faults reported
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <Database className="w-3.5 h-3.5 text-sky-200" /> Every catch weighed, logged and published
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
